import type { CareStateDomainDb, ClinicalScopeT, LinkT } from '../model/types';

import { actualLink } from './links';

/**
 * Resolve the problem ID for a request
 *
 * Returns null when:
 * - the request doesn't exist
 * - the request exists but does not link to a problem
 * - the request link discriminator is missing/unknown (we do not guess)
 */
export function resolveProblemIdForRequest(
  requestId: string,
  db: CareStateDomainDb
): string | null {
  const request = db.requests.find((r) => r.id === requestId);
  if (!request) return null;

  switch (request.link.type) {
    case 'problem':
      return request.link.id;
    case 'patient':
    case 'request':
    case 'orphan':
      return null;
    case undefined:
      return null;
    default: {
      const _exhaustive: never = request.link;
      return _exhaustive;
    }
  }
}

/**
 * Determine whether a resource with a given link is visible within a clinical scope.
 *
 * - First compute actual link (missing request/problem => orphan)
 * - Then apply visibility rules based on scope
 *
 * - If the (actual) link has a missing/unknown discriminator, we only treat it as visible
 *   in Patient scope (never in Problem/Request scope).
 */
export function isVisibleInScope(
  scope: ClinicalScopeT,
  resourceLink: LinkT,
  db: CareStateDomainDb
): boolean {
  const link = actualLink(resourceLink, db);

  if (scope.type === 'patient') {
    // Patient scope sees everything, including orphans.
    return true;
  }

  if (link.type === undefined) {
    // Avoid guessing in a narrowed scope.
    return false;
  }

  if (scope.type === 'problem') {
    switch (link.type) {
      case 'orphan':
        return false;
      case 'patient':
        return true;
      case 'problem':
        return scope.id === link.id;
      case 'request': {
        const problemId = resolveProblemIdForRequest(link.id, db);
        return problemId !== null && problemId === scope.id;
      }
      default: {
        return false;
      }
    }
  }

  if (scope.type === 'request') {
    const scopeProblemId = resolveProblemIdForRequest(scope.id, db);

    switch (link.type) {
      case 'orphan':
        return false;
      case 'patient':
        return true;
      case 'request':
        return scope.id === link.id;
      case 'problem':
        return scopeProblemId !== null && scopeProblemId === link.id;
      default: {
        return false;
      }
    }
  }
  return false;
}
